import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IniciativasIAServices } from "../../services/iniciativasIA";
import { formatFecha } from "./IniciativasTable";

// Vista de detalle de solo lectura (spec 008, Incremento 2, RF-42/CA-40/CA-41, plan.md §14.3 — T-APP-20).
//
// NO es un formulario: etiqueta y valor, CERO <input> (ni deshabilitados). Mismas 4 secciones del
// registro (spec.md §3) + Champion asignado + checklist + comentarios del flujo (historial con
// Comentario). Los campos vacíos se marcan "Sin información" en gris (CA-41). NINGÚN campo "Prioridad"
// (P5-bis, §7.1).
//
// Se alimenta de GET /iniciativas-ia/:id — el MISMO endpoint que usa IniciativaFormModal (D-16) — que ya
// trae los nombres resueltos (NombreCluster/NombreClubhouse/NombreTipoIA/NombrePostulante/
// NombreVigilancia/NombreEstado/NombreChampion, plan.md §18.6), así que no depende de `catalogos` salvo
// para los nombres de los ítems del checklist (el recordset del checklist solo trae IdChecklistItem/
// Marcado/Detalle, sin nombre).
const Campo = ({ label, value }) => {
  const [t] = useTranslation("iniciativasIA");
  return (
    <div className="mb-3">
      <label className="d-block text-muted mb-1">{label}</label>
      <div>
        {value ? value : <span className="text-muted">{t("iniciativasIA_detalle_sin_informacion")}</span>}
      </div>
    </div>
  );
};

// §18.5/§18.6: NombreChampion es cadena vacía (no NULL) cuando IdChampion IS NULL — comprobar el IdChampion,
// nunca `!NombreChampion`, para decidir si hay Champion asignado.
const claseEstadoHistorial = (codigo) => `estado-${String(codigo || "").toLowerCase()}`;

// RF-17 (spec 009): mismo código fijo que IniciativaFormModal.jsx — el ítem "Uso de datos" ya no muestra
// "¿Cuáles?" (texto libre) sino el Tier (0..4, etiquetas i18n del front, no del backend).
const CODIGO_USO_DATOS = "USO_DATOS";

const IniciativaDetalleModal = ({ show, onHide, iniciativaId, catalogos }) => {
  const [t] = useTranslation("iniciativasIA");
  const history = useHistory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !iniciativaId) {
      setData(null);
      return;
    }
    setLoading(true);
    IniciativasIAServices.getIniciativa(iniciativaId)
      .then((result) => setData(result))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [show, iniciativaId]);

  const checklistItems = catalogos?.checklistItems || [];
  const checklistData = data?.Checklist || [];
  const historial = data?.Historial || [];
  const comentariosDelFlujo = historial.filter((h) => h.Comentario);

  return (
    <Modal show={show} onHide={onHide} size="xl" enforceFocus={false} centered>
      <Modal.Header>
        <Modal.Title>
          <i className="fa fa-eye mr-2 text-primary"></i>
          {t("iniciativasIA_detalle_modal_title")}
        </Modal.Title>
        {/* `.close` de BS4, no `closeButton` de react-bootstrap: ese renderiza `.btn-close` (Bootstrap 5),
            que no existe en el CSS 4.6 que compila la app (DT-3) y queda invisible. */}
        <button
          type="button"
          className="close"
          aria-label={t("iniciativasIA_modal_close_aria")}
          onClick={onHide}
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </Modal.Header>
      <Modal.Body>
        {loading || !data ? (
          <div className="text-center py-5">
            <i className="fa fa-spinner fa-spin fa-2x text-primary"></i>
          </div>
        ) : (
          <>
            <div className="mb-3 d-flex align-items-center">
              <span className={`badge ${data.ClaseCss} mr-2`}>{data.NombreEstado}</span>
              <span className="text-muted">{data.Codigo}</span>
            </div>

            {/* ---- Sección 1 — Información General ---- */}
            <div className="mb-4">
              <h6>
                <i className="fa fa-id-card mr-2"></i>
                {t("iniciativasIA_section_general")}
              </h6>
              <div className="row">
                <div className="col-md-3">
                  <Campo label={t("iniciativasIA_field_id")} value={data.Codigo} />
                </div>
                <div className="col-md-6">
                  <Campo label={t("iniciativasIA_field_name")} value={data.Nombre} />
                </div>
                <div className="col-md-3">
                  <Campo label={t("iniciativasIA_field_date")} value={formatFecha(data.Fecha)} />
                </div>
              </div>
              <div className="row">
                <div className="col-md-3">
                  <Campo label={t("iniciativasIA_field_cluster")} value={data.NombreCluster} />
                </div>
                <div className="col-md-3">
                  <Campo label={t("iniciativasIA_field_applicant")} value={data.NombrePostulante} />
                </div>
                <div className="col-md-3">
                  <Campo label={t("iniciativasIA_field_sponsor")} value={data.Sponsor} />
                </div>
                <div className="col-md-3">
                  <Campo label={t("iniciativasIA_field_clubhouse")} value={data.NombreClubhouse} />
                </div>
              </div>
            </div>

            {/* ---- Sección 2 — Detalle de la Iniciativa ---- */}
            <div className="mb-4">
              <h6>
                <i className="fa fa-align-left mr-2"></i>
                {t("iniciativasIA_section_detail")}
              </h6>
              <Campo label={t("iniciativasIA_field_description")} value={data.Descripcion} />
              <div className="row">
                <div className="col-md-6">
                  <Campo label={t("iniciativasIA_field_justification")} value={data.Justificacion} />
                </div>
                <div className="col-md-6">
                  <Campo label={t("iniciativasIA_field_background")} value={data.Antecedentes} />
                </div>
              </div>
            </div>

            {/* ---- Sección 3 — Beneficios ---- */}
            <div className="mb-4">
              <h6>
                <i className="fa fa-hand-holding-usd mr-2"></i>
                {t("iniciativasIA_section_benefits")}
              </h6>
              <Campo label={t("iniciativasIA_field_benefits")} value={data.Beneficios} />
              <div className="row">
                <div className="col-md-5">
                  {/* RF-16 (spec 009): ahora resuelto por SP_INIA_CONSULTAR_DETALLE (NombreEstrategia),
                      no la columna vieja AlineacionEstrategica (sin uso desde este incremento). */}
                  <Campo
                    label={t("iniciativasIA_field_strategic_alignment")}
                    value={data.NombreEstrategia}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    label={t("iniciativasIA_field_beneficiary_users")}
                    value={data.UsuariosBeneficiados}
                  />
                </div>
                <div className="col-md-3">
                  <Campo label={t("iniciativasIA_field_vigilancia")} value={data.NombreVigilancia} />
                </div>
              </div>
              <Campo
                label={t("iniciativasIA_field_vigilancia_just")}
                value={data.VigilanciaJustificacion}
              />
            </div>

            {/* ---- Sección 4 — Definición ---- */}
            <div className="mb-4">
              <h6>
                <i className="fa fa-microchip mr-2"></i>
                {t("iniciativasIA_section_definition")}
              </h6>
              <div className="row">
                <div className="col-md-4">
                  <Campo label={t("iniciativasIA_field_ai_type")} value={data.NombreTipoIA} />
                </div>
              </div>

              <div className="mb-3">
                <table className="table table-bordered checklist-ia mb-0">
                  <caption className="checklist-ia-caption">{t("iniciativasIA_checklist_title")}</caption>
                  <tbody>
                    {checklistItems.map((item) => {
                      const entry = checklistData.find((c) => c.IdChecklistItem === item.IdChecklistItem);
                      return (
                        <tr key={item.IdChecklistItem}>
                          <td>{item.Nombre}</td>
                          <td className="text-center" style={{ width: "80px" }}>
                            <i
                              className={`fa ${
                                entry?.Marcado ? "fa-check text-success" : "fa-times text-muted"
                              }`}
                            ></i>
                          </td>
                          <td>
                            {item.Codigo === CODIGO_USO_DATOS ? (
                              // RF-17/RF-17-bis: el número guardado se mapea a su etiqueta i18n (constante
                              // del front, no catálogo de BD), solo si el ítem está marcado.
                              entry?.Marcado && data.TierUsoDatos !== null && data.TierUsoDatos !== undefined ? (
                                t(`iniciativasIA_tier_${data.TierUsoDatos}`)
                              ) : entry?.Marcado ? (
                                <span className="text-muted">
                                  {t("iniciativasIA_detalle_sin_informacion")}
                                </span>
                              ) : null
                            ) : item.RequiereDetalle ? (
                              entry?.Detalle ? (
                                entry.Detalle
                              ) : (
                                <span className="text-muted">
                                  {t("iniciativasIA_detalle_sin_informacion")}
                                </span>
                              )
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <Campo label={t("iniciativasIA_field_expected_result")} value={data.ResultadoEsperado} />
            </div>

            {/* ---- Champion asignado (RF-42(d)) ---- */}
            <div className="mb-4">
              <h6>
                <i className="fa fa-user-check mr-2"></i>
                {t("iniciativasIA_detalle_section_champion")}
              </h6>
              <Campo
                label={t("iniciativasIA_detalle_field_champion")}
                value={data.IdChampion ? data.NombreChampion : null}
              />
            </div>

            {/* ---- Ir a cronograma (spec 009, RF-8/CA-10): solo cuando la iniciativa está Habilitada
                 y tiene un proyecto vinculado. data.CodigoEstado/data.IdProyecto ya vienen del MISMO
                 GET (SP_INIA_CONSULTAR_DETALLE) — cero cambio de API/SP. Mismo patrón btn-link que "Volver"
                 en IniciativaAccionesModal.jsx. */}
            {data.CodigoEstado === "HABILITADO" && data.IdProyecto && (
              <div className="mb-4">
                <button
                  type="button"
                  className="btn btn-link p-0"
                  onClick={() => history.push(`/schedule/${data.IdProyecto}`)}
                >
                  <i className="fa fa-calendar-alt mr-2"></i>
                  {t("iniciativasIA_detalle_ir_cronograma")}
                </button>
                <p className="text-muted small mb-0">{t("iniciativasIA_detalle_ir_cronograma_desc")}</p>
              </div>
            )}

            {/* ---- Comentarios del flujo (RF-42(d)): motivos de devolución y de habilitación, con
                 autor y fecha -- a diferencia del aviso rojo de RF-33, aquí SÍ se muestra todo lo
                 recopilado (CA-41 exige "absolutamente toda la información", no un subconjunto). ---- */}
            {comentariosDelFlujo.length > 0 && (
              <div className="mb-0">
                <h6>
                  <i className="fa fa-comments mr-2"></i>
                  {t("iniciativasIA_detalle_section_comentarios")}
                </h6>
                {comentariosDelFlujo.map((h, idx) => (
                  <div key={idx} className="border-left pl-3 mb-3">
                    <div>
                      <span className={`badge ${claseEstadoHistorial(h.CodigoEstadoNuevo)} mr-2`}>
                        {h.NombreEstadoNuevo}
                      </span>
                      <small className="text-muted">
                        {formatFecha(h.Fecha)} · {h.NombreUsuario}
                      </small>
                    </div>
                    <div>{h.Comentario}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-dark" onClick={onHide}>
          {t("iniciativasIA_detalle_btn_close")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default IniciativaDetalleModal;
